import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Accordion, AccordionHeader, AccordionItem, AccordionPanel,
  Button, Caption1,
  Field, Input, MessageBar, MessageBarBody, Select, Text, Textarea, makeStyles,
} from '@fluentui/react-components';
import { type Person, type UpdatePerson } from '@vigeo/shared';
import { PopupDialog, useSectionActive, KeyValueList, shellMedia, tokens, type KeyValueItem } from '@vigeo/ui';
import { updatePerson } from '@/lib/api.js';
import {
  detailFields, detailSectionTitles, personDetailsPatch, personDetailsValues, type PersonDetailsSection,
} from './person-details-form.js';
import { PersonRoleEditor } from './PersonRoleEditor.js';
import { SensitiveIdentityFields } from './SensitiveIdentityFields.js';

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  actions: { display: 'flex', justifyContent: 'flex-end' },
  form: {
    display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalM,
    [shellMedia.phone]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
});

export interface PersonDetailsPanelProps {
  readonly person: Person;
  readonly canEdit: boolean;
}

type EditState = {
  readonly section: PersonDetailsSection;
  readonly original: Readonly<Record<string, string>>;
  readonly draft: Readonly<Record<string, string>>;
};

/** Current staff-maintained details; immutable candidate submissions are never edited here. */
export function PersonDetailsPanel({ person, canEdit }: PersonDetailsPanelProps) {
  const sectionActive = useSectionActive();
  const styles = useStyles();
  const client = useQueryClient();
  const [edit, setEdit] = useState<EditState | null>(null);
  /** The button that opened the dialog, so the keyboard goes back there (Safari on a Mac does not focus a button on click). */
  const opener = useRef<HTMLElement | null>(null);
  /** Anything different from when the dialog opened (typing a value and putting it back counts as nothing). */
  const typed = edit !== null && Object.keys(edit.draft).some((path) => edit.draft[path] !== edit.original[path]);
  const [validation, setValidation] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (patch: UpdatePerson) => updatePerson(person.id, patch),
    onSuccess: async () => {
      setEdit(null);
      await Promise.all([
        ['person', person.id], ['people'], ['checklist', person.id], ['dashboard'], ['audit', person.id],
      ].map((queryKey) => client.invalidateQueries({ queryKey })));
    },
  });

  function open(section: PersonDetailsSection, button: HTMLElement) {
    opener.current = button;
    const original = personDetailsValues(person, section);
    setEdit({ section, original, draft: original });
    setValidation(null);
    save.reset();
  }

  function submit() {
    if (!canEdit || edit === null) return;
    try {
      const patch = personDetailsPatch(edit.original, edit.draft, edit.section);
      setValidation(null);
      if (Object.keys(patch).length === 0) { setEdit(null); return; }
      save.mutate(patch);
    } catch (error) {
      // The shared update schema validates the same payload the API will receive.
      if (error instanceof Error && 'issues' in error && Array.isArray(error.issues)) {
        setValidation(error.issues.map((issue: { path: Array<string | number>; message: string }) => {
          const label = detailFields[edit.section].find((field) => field.path === issue.path.join('.'))?.label;
          return `${label ?? issue.path.join('.')}: ${issue.message}`;
        }).join(' · '));
      } else setValidation('Check the details and try again.');
    }
  }

  function readItems(section: PersonDetailsSection): KeyValueItem[] {
    const values = personDetailsValues(person, section);
    const items: KeyValueItem[] = detailFields[section].map((field) => {
      const value = values[field.path] ?? '';
      return { label: field.label, value: field.choices?.find(([key]) => key === value)?.[1] ?? (value || 'Not recorded') };
    });
    if (section === 'assignment') items.push({ label: 'Employee ID', value: person.employeeId ?? 'Not assigned' });
    if (section === 'emr') items.push(
      { label: 'First provisioned', value: person.emr?.provisionedAt ? new Date(person.emr.provisionedAt).toLocaleString() : 'Not recorded' },
      { label: 'Provisioned by', value: person.emr?.provisionedBy ?? 'Not recorded' },
    );
    return items;
  }

  const sections = ['contact', 'assignment', 'emr'] as const;
  return (
    <section className={styles.panel} aria-label="Personnel details">
      <div>
        <Text weight="semibold">Personnel details</Text><br />
        <Caption1>Current HR records. Submitted application answers remain in the original submission history.</Caption1>
      </div>
      <PersonRoleEditor person={person} canEdit={canEdit} />
      <Accordion multiple collapsible>
        {sections.map((section) => (
          <AccordionItem key={section} value={section}>
            <AccordionHeader>{detailSectionTitles[section]}</AccordionHeader>
            <AccordionPanel>
              <div className={styles.section}>
                <KeyValueList aria-label={detailSectionTitles[section]} items={readItems(section)} />
                {/*
                  The date of birth is not a row of this list and not a field of its form (#606): the person record a browser
                  receives has none. What stands here is ADR-012's pattern: whether one is on file, masked, and the recorded
                  Reveal. `canEdit` is the admin flag this screen has; the server decides who may reveal whatever it says, and
                  the component draws nothing where the protected store is switched off.
                */}
                {section === 'contact' ? <SensitiveIdentityFields personId={person.id} canReveal={canEdit} /> : null}
                {canEdit ? <div className={styles.actions}><Button onClick={(event) => open(section, event.currentTarget)}>Edit {detailSectionTitles[section].toLowerCase()}</Button></div> : null}
              </div>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
      <Caption1>Last updated {new Date(person.updatedAt).toLocaleString()} by {person.updatedBy}</Caption1>

      {/*
        INTERIM, AND TOO LONG FOR A DIALOG. The pop-up rules (PG10) put a form of more than 5 fields in the page, under what opened
        it; this one has 26 in Contact and 10 in Assignment. It stays a dialog, on the large size with a scrolling middle, until
        Julio decides where it goes (HR LEAD 3 has the question). Everything else the rules ask of a dialog applies now: Cancel left
        and the one blue button right, the keyboard on the first field, X/Esc/click outside asking "Discard what you typed?" when
        anything changed, nothing closing while it saves, and the keyboard back on the button that opened it.

        WHAT IS SAVED IS UNCHANGED. `submit` below and `personDetailsPatch` are as they were; this only changes the shell.
        `sectionActive` is kept: the dialog closes while its section is not the one showing and comes back, with what was typed,
        when it is (`tests/person-forms-popup.test.tsx`).
      */}
      <PopupDialog
        open={edit !== null && canEdit && sectionActive}
        title={edit === null ? 'Edit personnel details' : `Edit ${detailSectionTitles[edit.section].toLowerCase()}`}
        size="large"
        opener={opener}
        dirty={typed}
        busy={save.isPending}
        onClose={() => setEdit(null)}
        primary={{ label: 'Save details', busyLabel: 'Saving…', type: 'submit', form: 'person-details-form' }}
      >
        <form id="person-details-form" onSubmit={(event) => { event.preventDefault(); submit(); }} className={styles.panel}>
          {validation || save.isError ? <MessageBar intent="error"><MessageBarBody>{validation ?? save.error?.message}</MessageBarBody></MessageBar> : null}
          {edit?.section === 'contact' ? <Caption1>For each emergency contact, enter a name, relationship and phone, or leave the whole contact blank.</Caption1> : null}
          {edit?.section === 'emr' ? <Caption1>Record the account identifier and setup status. Do not enter passwords or access codes.</Caption1> : null}
          <div className={styles.form}>
            {edit === null ? null : detailFields[edit.section].map((field) => {
              const value = edit.draft[field.path] ?? '';
              const change = (next: string) => setEdit({ ...edit, draft: { ...edit.draft, [field.path]: next } });
              return <Field key={field.path} label={field.label}>
                {field.choices ? <Select value={value} disabled={save.isPending} onChange={(_, data) => change(data.value)}>
                  {field.choices.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </Select> : field.multiline ? <Textarea value={value} disabled={save.isPending} onChange={(_, data) => change(data.value)} />
                  : <Input type={field.type ?? 'text'} value={value} disabled={save.isPending} onChange={(_, data) => change(data.value)} />}
              </Field>;
            })}
          </div>
        </form>
      </PopupDialog>
    </section>
  );
}
